/**
 * The shell every list-control menu wears.
 *
 * The Fields panel set the shape — a titled header with a count, an optional
 * search box, a scrolling body of rows that each carry their column's icon,
 * and a footer with Reset on the left. Group, Sort and Summarize were three
 * smaller menus in three slightly different shapes, which is how a settings
 * area stops feeling like one settings area.
 *
 * So the shell is here and the four panels fill it. Width, padding, the border
 * under the header, where Reset sits and what the hint on the right says are
 * decided once.
 */
import { useState, type ReactNode } from 'react'
import { SectionLabel } from './primitives.js'
import { COLUMN_ICONS, IconChevronLeft, IconSearch } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

/** Every control menu is this wide, so the gear's panels do not jump about. */
export const PANEL_WIDTH = 304

/** And every row in one is this tall, whatever control sits on its right. */
export const PANEL_ROW = 28

export interface ControlPanelProps {
  /**
   * Render the rows and nothing else — no header, no footer, no height cap.
   *
   * On a phone the panel is a `PanelSheet`, and the sheet already owns all
   * three: a 56px header with the grab handle and a ✕ a thumb can hit, a
   * sticky footer above the safe area holding Reset and Done, and 88dvh.
   * Drawing this shell inside that one gives two headers, two titles and a
   * scroll area inside a scroll area. The *rows* are the part worth sharing,
   * so they are the part that crosses — which is what keeps the two shapes
   * from drifting (§6.3).
   */
  bare?: boolean
  title: string
  /** The right-hand figure in the header — "12 of 31 shown". */
  count?: string
  /** Shown when the panel offers a search box. */
  search?: { value: string; onChange: (value: string) => void; placeholder: string }
  children: ReactNode
  /**
   * Returns to List settings. Every one of these panels is a sub-menu of that
   * one, and a sub-menu you can only leave by closing and re-opening the gear
   * is a sub-menu that costs three clicks to change your mind about.
   */
  onBack?: () => void
  /** Puts everything back to the area's defaults. */
  onReset?: () => void
  resetLabel?: string
  hint?: string
}

export function ControlPanel({
  bare, title, count, search, children, onBack, onReset, resetLabel = 'Reset',
  hint = 'Changes apply as you go',
}: ControlPanelProps) {
  /*
   * In a sheet: the search well at the board's 42px, the rows, and nothing
   * else. No header, because the sheet has one; no footer hint, because a
   * line of 11px grey text at the bottom of a phone screen is not read; and
   * **no Reset**, because board 6 puts it in the sheet's own footer beside
   * Done where a thumb is already going.
   *
   * It is a separate branch rather than a pile of conditionals through the
   * desktop one so that what a sheet draws can be read in one place.
   */
  if (bare) {
    return (
      <>
        {search && <PanelSearch {...search} tall />}
        {children}
      </>
    )
  }

  return (
    /*
      * Capped so the footer stays on screen. A popover opens a third of the way
      * down the page, so 70vh of panel put Reset below the fold — and a panel
      * whose Reset you cannot reach is a panel you cannot undo.
      */
    <div className="flex max-h-panel flex-col">
      {/*
        * One header, whether or not there is a way back.
        *
        * A fixed height and a reserved chevron slot, because the alternative
        * is what this was: the root menu's title sat higher and further left
        * than the titles of the four menus inside it, so going into Group and
        * back out again moved the words. The slot is 24px of nothing on the
        * root menu, which is cheaper than a title that jumps.
        */}
      <header className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-line pl-1.5 pr-3">
        <span className="flex min-w-0 items-center gap-1">
          {onBack
            ? (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back to list settings"
                title="Back to list settings"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-field text-text-muted hover:bg-line-soft hover:text-text"
              >
                <IconChevronLeft size={15} />
              </button>
            )
            : <span aria-hidden className="h-6 w-6 shrink-0" />}
          <span className="truncate text-card">{title}</span>
        </span>
        {count && <span className="shrink-0 text-sub text-text-muted">{count}</span>}
      </header>

      {search && <PanelSearch {...search} />}

      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

      <footer className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
        {onReset
          ? (
            <button
              type="button"
              onClick={onReset}
              className="text-cell font-semibold text-accent hover:text-accent-dark"
            >
              {resetLabel}
            </button>
          )
          : <span />}
        <span className="text-sub text-text-faint">{hint}</span>
      </footer>
    </div>
  )
}

/**
 * The one search well a control panel draws, at two heights.
 *
 * 36px in a popover and 42px in a sheet — the board's number, because a box
 * you type an address into with a thumb is not the box you type into with a
 * mouse. Everything else about it is the same, which is the reason it is one
 * component: the ring belongs to the box rather than to the inner input (see
 * `AddSearch`), and that is the detail that gets forgotten when it is copied.
 */
export function PanelSearch({
  value, onChange, placeholder, tall,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  tall?: boolean
}) {
  return (
    <label
      data-panel-search
      className={cn('relative flex shrink-0 items-center border-b border-line-soft',
        tall ? 'h-[42px] px-3' : 'h-9 px-2',
        'focus-within:ring-2 focus-within:ring-inset focus-within:ring-accent-ring')}
    >
      <span className="sr-only">{placeholder}</span>
      <span
        aria-hidden
        className={cn('pointer-events-none absolute text-text-muted', tall ? 'left-5' : 'left-3.5')}
      >
        <IconSearch size={tall ? 16 : 14} />
      </span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={cn('h-full w-full bg-transparent text-text',
          tall ? 'pl-7 text-body' : 'pl-6 text-cell',
          'placeholder:text-text-muted focus:outline-none focus-visible:shadow-none')}
      />
    </label>
  )
}

/** A titled block inside a panel — "Shown · drag to reorder", "Hidden". */
export function PanelSection({
  label, children, first,
}: { label: string; children: ReactNode; first?: boolean }) {
  return (
    <section className={cn('px-3 py-2', !first && 'border-t border-line-soft')}>
      <SectionLabel>{label}</SectionLabel>
      <ul className="mt-1.5 flex list-none flex-col gap-0.5 p-0">{children}</ul>
    </section>
  )
}

/**
 * One field in a panel: its column's icon, its label, and whatever the panel
 * puts on the right.
 *
 * The icon is the point. A menu that lists "Job, Status, Next action" in plain
 * text is a menu you read; with the icons it is a menu you recognise, and it
 * is the same glyph the column header wears three inches below.
 */
export function PanelRow({
  field, label, detail, selected, onClick, children,
}: {
  field?: ListFieldDef
  /** Used when the row is not about a field — "No grouping", "Default order". */
  label?: string
  detail?: ReactNode
  selected?: boolean
  onClick?: () => void
  /** A control on the right, for panels that are not a list of choices. */
  children?: ReactNode
}) {
  const Icon = field ? COLUMN_ICONS[field.icon] : null
  const text = label ?? field?.label ?? ''

  const body = (
    <>
      {Icon
        ? <span className="shrink-0 text-text-muted"><Icon size={14} /></span>
        : <span aria-hidden className="w-[14px] shrink-0" />}
      <span className="min-w-0 flex-1 truncate text-cell">{text}</span>
      {detail && <span className="shrink-0 text-sub text-text-muted">{detail}</span>}
      {children}
    </>
  )

  /*
   * `PANEL_ROW` everywhere, not padding. The Fields panel's rows are 28px
   * because a Switch is 20px inside `py-1`; Group's were 24 and Summarize's 36
   * because a `<select>` is taller again. Three menus behind one gear, at three
   * pitches. One number fixes it and keeps fixing it.
   */
  if (!onClick) {
    return (
      <li
        style={{ minHeight: PANEL_ROW }}
        className="flex items-center gap-1.5 px-1 text-text"
      >
        {body}
      </li>
    )
  }

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        style={{ minHeight: PANEL_ROW }}
        className={cn(
          'flex w-full items-center gap-1.5 rounded-field px-1 text-left',
          selected ? 'bg-accent-soft font-semibold text-accent' : 'text-text hover:bg-surface-alt',
        )}
      >
        {body}
      </button>
    </li>
  )
}

/**
 * Which of the gear's panels is open.
 *
 * It lives here rather than in `list-controls.tsx` because `panelFacts`
 * below does, and that one is read by `list-fields.tsx` too — which
 * `list-controls` imports, so putting it there would be a cycle.
 */
export type GearPanel = null | 'gear' | 'group' | 'sort' | 'fields' | 'sum' | 'filter'

/**
 * What Reset means in each of the gear's panels, and what it is called.
 *
 * One table rather than an expression inside each panel, because in a sheet
 * the control is not in the panel: board 6 puts it in the footer beside Done,
 * where a thumb is already going, and the footer belongs to `PanelSheet`. Two
 * copies of "does this list have a grouping to undo" is two copies that can
 * come to different answers.
 *
 * `null` where there is nothing to undo, so the control is absent rather than
 * disabled — a greyed Reset is still a Reset you reach for.
 */
export interface PanelReset { label: string; run: () => void }

export interface PanelFacts {
  /** The gear row's right-hand word — "None", "Default", "6 of 27". */
  value: string
  /** The header's figure, which says what it is counting — "6 of 27 shown". */
  count: string
  /** Absent where there is nothing to undo. */
  reset: PanelReset | null
}

/**
 * What each of the gear's panels currently says, and what Reset does to it.
 *
 * One function, three readers: the gear row's value, the panel header's
 * count, and the Reset in whichever footer this shape has. All three were
 * their own expression — "does this list have a grouping" written out in
 * three places — and three copies of one question is three answers waiting
 * to disagree. It is here rather than inside each panel because in a sheet
 * the Reset is not in the panel at all: board 6 puts it in the footer beside
 * Done, and the footer belongs to `PanelSheet`.
 */
export function panelFacts(panel: GearPanel, list: UseListResult): PanelFacts {
  switch (panel) {
    case 'group': {
      const name = list.view.group
        ? list.fields.find((field) => field.name === list.view.group)?.label
        : undefined
      return {
        value: name ?? 'None',
        count: name ?? 'None',
        reset: list.view.group
          ? { label: 'Ungroup', run: () => list.setGroup(undefined) }
          : null,
      }
    }
    case 'sort': {
      const on = list.view.sort.length
      const word = on > 0 ? plural(on, 'column') : 'Default'
      return {
        value: word,
        count: word,
        reset: on > 0 ? { label: 'Default order', run: () => list.setSort([]) } : null,
      }
    }
    case 'fields': {
      const shown = list.visibleFields.length
      const all = list.fields.filter((f) => f.selectable !== false).length
      return {
        value: `${shown} of ${all}`,
        count: `${shown} of ${all} shown`,
        reset: {
          label: 'Reset',
          run: () => list.setView({ ...list.view, fields: undefined, pinned: undefined }),
        },
      }
    }
    case 'sum': {
      const on = list.view.aggregates.length
      const word = on > 0 ? plural(on, 'column') : 'None'
      return {
        value: word,
        count: word,
        reset: on > 0
          ? { label: 'Clear totals', run: () => list.setView({ ...list.view, aggregates: [] }) }
          : null,
      }
    }
    default: {
      const on = (list.view.filter?.conditions ?? []).length
      const word = on > 0 ? plural(on, 'rule') : 'None'
      return { value: word, count: word, reset: null }
    }
  }
}

export const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

/** The search box's state, since three panels want the same one. */
export function usePanelSearch() {
  const [query, setQuery] = useState('')
  const matches = (field: ListFieldDef) =>
    field.label.toLowerCase().includes(query.trim().toLowerCase())
  return { query, setQuery, matches }
}
